/* eslint-disable max-lines */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Menu } from "antd";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import config from "appConfig";
import styles from "./styles/SearchBar.scss";
import { getIconUrl } from "../../services/asset";
import {
  selectSuggestions,
  getSuggestions,
  setSearchText,
  selectSearchText
} from "../../routes/Ad/ad.ducks";
import {
  selectIsFocus,
  updateCriteriaModules,
  setIsFocus,
  selectSearchLoading,
  setSearchLoading,
} from "../../routes/Search/search.ducks";
import history from "../../history";
import {
  PageRoute,
  LocalStorageKey,
  AdsSortOption,
  LocaleCode,
  ElementId,
  StyleProperty,
  QueryParam,
  SearchMode
} from "../../models";
import moment from "moment";

import {
  api,
  stringifyURI,
  localStorage,
  notification,
} from "../../services";
import SearchInput from "./SearchInput";
import SearchBarSuggestion from "../../utils/SearchBarSuggestion";
import CategoryMatchSuggestion from "../../utils/CategoryMatchSuggestion";
import { StaticHTML } from "../../components";
import { useOutsideAlerter } from "../../utils/useOutsideClick";
import { mapCategoryToHr, mapModuleToHr } from "../../routes/LanguageMapper";
import usePrevious from "../../utils/usePrevious";
import SearchBarIconOnly from "./SearchBarIconOnly";
import { CloseIcon } from "./CloseIcon";
import { ChevronLeftIcon } from "./ChevronLeftIcon";
import { useTrackingEvents } from "./hooks/useTrackingEvents";
import { useMomentLocale } from "./hooks/useMomentLocale";
import { useHandleKeyUp } from "./hooks/useHandleKeyUp";
import { useInitializeSearchBar } from "./hooks/useInitializeSearchBar";
import { useRenderedInputTrigger } from "./hooks/useRenderedInputTrigger";
import { useMobileStyleBody } from "./hooks/useMobileStyleBody";
import { useAnimatedPlaceholder } from "./hooks/useAnimatedPlaceholder";
import { useAiSuggestions } from "./hooks/useAiSuggestions";
import { selectIsAiMode } from "../App/selectors";
import { updateSearchMode } from "../App/actions";

const appendSearchTracking = (url: string, searchType: string, query?: string): string => {
  const separator = url.includes("?") ? "&" : "?";
  let result = `${url}${separator}search_type=${searchType}`;
  if (query) {
    result += `&search_query=${encodeURIComponent(query)}`;
  }
  return result;
};

const unfocusTimeout = 500;
let rootElement = document.getElementById(ElementId.root);
function SearchBar(props) {
  const getSuggestionTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const {
    setSearchText,
    searchText,
    getSuggestions,
    isMobile,
    searchResult,
    className,
    iconOnly,
    isFocus,
    setIsFocus,
    isHomePage,
    clearCriteriaModules,
    isSearchLoading,
    setSearchLoadingRedux,
    isAiMode,
    setSearchMode,
  } = props;
  const emptyObject = useRef({});
  useMomentLocale()

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (getSuggestionTimeoutRef.current) {
        clearTimeout(getSuggestionTimeoutRef.current);
      }
    };
  }, []);
  const [selectedMenu, setSelectedMenu] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const [error, setError] = useState(false);
  const [showMobileStyle, setShowMobileStyle] = useState(false);
  const [recentQueryParams, setRecentQueryParams] = useState(emptyObject.current);
  const [loading, setLoading] = useState(false);
  const [actionTime, setActionTime] = useState(0);
  const [tempSearchResult, setTempSearchResult] = useState(emptyObject.current);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showSearchModeDropdown, setShowSearchModeDropdown] = useState(false);
  const [glowAnimationPlayed, setGlowAnimationPlayed] = useState(false);
  // Highlighted AI suggestion index, kept separate from the classic-mode selectedMenu
  // because that one is indexed against categoryMatch/category ordering.
  const [aiSelectedMenu, setAiSelectedMenu] = useState(-1);
  // searchText/isAiMode/isFocus all come from redux, so every mounted SearchBar
  // sees the same keystrokes. On mobile the home page bar and the header bar are
  // both mounted, and only the one showing the full-screen overlay is driving the
  // input, so only that one should fetch suggestions (V2OGLASI-2576).
  const ownsAiSuggestions = isMobile ? showMobileStyle : !iconOnly;
  // TEMP DEBUG (V2OGLASI-2576) - remove once the duplicate request is understood
  // eslint-disable-next-line no-console
  const { aiSuggestions, aiSuggestionsLoading } = useAiSuggestions(searchText, isAiMode, ownsAiSuggestions);
  const { handleSearchTracking, handleSuggestedOptionTracking, handleRecentSearchTracking, trackClickEvent, trackSearchModeChange, trackViewingOfSearchResults } = useTrackingEvents()
  const triggerRenderedInput = useRenderedInputTrigger(focusToInput)
  const inputRef = useRef();
  const menuRef = useRef();
  const searchModeDropdownRef = useRef();
  const previousSearchText = usePrevious(searchText)
  const showAnimatedPlaceholderDesktop = isAiMode && isHomePage && !isFocus && searchText.trim() === "";
  // Mirror the desktop behaviour: the animated placeholder is a hint shown
  // before the user commits to typing, so it also hides on focus on mobile.
  const showAnimatedPlaceholderMobile = isAiMode && showMobileStyle && !isFocus && searchText.trim() === "";
  const { displayText: placeholderText, cursorVisible } = useAnimatedPlaceholder(showAnimatedPlaceholderDesktop || showAnimatedPlaceholderMobile);

  // Close search mode dropdown on outside click
  useEffect(() => {
    if (!showSearchModeDropdown) return;
    const handleClickOutside = (e) => {
      if (searchModeDropdownRef.current && !searchModeDropdownRef.current.contains(e.target)) {
        setShowSearchModeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearchModeDropdown]);

  // Mark glow animation as played after it completes (3s rotation + 0.3s fade)
  // Run once on mount, don't reset on focus changes
  useEffect(() => {
    if (isAiMode && isHomePage) {
      const timer = setTimeout(() => {
        setGlowAnimationPlayed(true);
      }, 3300);
      return () => clearTimeout(timer);
    }
  }, []);

  const addRootScrollBar = () => {
    rootElement.style.removeProperty(StyleProperty.overflowY);
    rootElement.style.removeProperty(StyleProperty.height);
  };

  useInitializeSearchBar(
    recentSearches,
    setRecentSearches,
    addRootScrollBar,
    setRecentQueryParams,
    history,
    isFocus,
    setIsFocus,
    searchText,
    setSearchText,
    getSuggestions
  )

  useOutsideAlerter(menuRef, inputRef, () => {
    if (!showMobileStyle) {
      handleInputBlur()
    } else {
      focusToInput();
    }
  });


  useEffect(() => {
    if (searchResult && searchResult.actionTime === actionTime) {
      setTempSearchResult(searchResult);
      setLoading(false);
    }
  }, [searchResult, actionTime]);

  const { suggestions, alternatives, enhancedSuggestions } = tempSearchResult;
  const categoryMatchCount = enhancedSuggestions && enhancedSuggestions.categoryMatchSuggestions
    ? enhancedSuggestions.categoryMatchSuggestions.length
    : 0;




  useEffect(() => {
    if (inputRef && inputRef.current && !isFocus) {
      // @ts-ignore
      inputRef.current.input.blur();
    }
  }, [isFocus]);

  // Prevent body scroll when mobile search is open

  useMobileStyleBody(showMobileStyle)

  // Auto-focus input when mobile search expands
  useEffect(() => {
    if (showMobileStyle && inputRef.current) {
      // Small delay to ensure the mobile UI has rendered
      setTimeout(() => {
        focusToInput();
      }, 50);
    }
  }, [showMobileStyle]);

  // Handle browser back button to close mobile search
  useEffect(() => {
    if (!showMobileStyle) return;

    // Push a state so back button can close the search
    window.history.pushState({ mobileSearchOpen: true }, '');

    const handlePopState = (e) => {
      // Close mobile search when back button is pressed
      setShowMobileStyle(false);
      setIsFocus(false);
      addRootScrollBar();
      if (inputRef.current && inputRef.current.input) {
        inputRef.current.input.blur();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showMobileStyle]);

  const searchAlternatives = suggestion => {
    const queryParams = {
      text: suggestion,
      sortOption: AdsSortOption.mostRelevant
    };

    // GA4 event for tracking search terms (alternative suggestion)
    trackViewingOfSearchResults({
      search_term: suggestion ? suggestion.trim() : "",
      search_type: SearchMode.classic
    })

    addRecentSearch(suggestion);
    setShowMobileStyle(false);
    addRootScrollBar();
    history.push(appendSearchTracking(`${PageRoute.search}${stringifyURI(queryParams, QueryParam.searchQuery)}`, SearchMode.classic, suggestion));
  };

  const handleAiSearch = useCallback(async (queryText?: string) => {
    const text = (typeof queryText === "string" ? queryText : searchText).trim();
    if (!text) {
      setError(true);
      return;
    }

    // When the query comes from a suggestion, reflect it in the input
    if (typeof queryText === "string") {
      setSearchText(text);
    }

    setAiLoading(true);
    setSearchLoadingRedux(true);
    handleSearchTracking(true);

    try {
      const response = await api.AiSearch.query(text);

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      const { resultUrlPath } = response;

      if (resultUrlPath) {
        const relativeUrl = resultUrlPath;

        addRecentSearch(text, null, null, relativeUrl, true);
        clearCriteriaModules();
        setError(false);
        setShowMobileStyle(false);
        addRootScrollBar();
        handleInputBlur();

        trackViewingOfSearchResults({
          search_term: text,
          category: "ai-search",
          search_type: SearchMode.ai
        });

        history.push(appendSearchTracking(relativeUrl, SearchMode.ai, text));
      } else {
        // AI couldn't generate a query, fall back to classic search
        handleClassicSearch(text);
      }
    } catch (err) {
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      // Check for rate limit error (code 1064)
      if (err && err.code === 1064) {
        // Rate limited - show message and fall back
        notification.showError("Prekoračen je limit AI pretraga. Pokušajte ponovo za minutu.");
      }
      // Fall back to classic search
      handleClassicSearch(text);
    } finally {
      setSearchLoadingRedux(false);
      if (isMountedRef.current) {
        setAiLoading(false);
      }
    }
  }, [searchText, clearCriteriaModules]);

  const handleClassicSearch = (queryText?: string) => {
    // Fallback from AI search - do a simple text search without relying on suggestions
    const text = (typeof queryText === "string" ? queryText : searchText).trim();
    if (!text) {
      setError(true);
      return;
    }
    addRecentSearch(text);
    clearCriteriaModules();
    setError(false);
    setShowMobileStyle(false);
    addRootScrollBar();
    handleInputBlur();

    const queryParams = {
      text,
      sortOption: AdsSortOption.mostRelevant
    };
    history.push(
      appendSearchTracking(`${PageRoute.search}${stringifyURI(queryParams, QueryParam.searchQuery)}`, SearchMode.classic, text)
    );
  };

  const handleSearch = (all?) => {
    if (loading) return;

    handleSearchTracking(isAiMode)

    const { searchText, clearCriteriaModules } = props;

    // Check if a CategoryMatchSuggestion is selected (selectedMenu is in range 0 to categoryMatchCount-1)
    if (selectedMenu >= 0 && selectedMenu < categoryMatchCount &&
      enhancedSuggestions && enhancedSuggestions.categoryMatchSuggestions &&
      enhancedSuggestions.categoryMatchSuggestions[selectedMenu]) {
      const selectedCategoryMatch = enhancedSuggestions.categoryMatchSuggestions[selectedMenu];
      const categoryUrl = selectedCategoryMatch.url;
      addRecentSearch(searchText, null, null, categoryUrl.startsWith('/') ? categoryUrl : `/${categoryUrl}`);
      clearCriteriaModules();
      setError(false);
      setShowMobileStyle(false);
      addRootScrollBar();
      handleInputBlur();

      // Navigate to the CategoryMatchSuggestion URL
      const url = selectedCategoryMatch.url;
      history.push(appendSearchTracking(url.startsWith('/') ? url : `/${url}`, SearchMode.classic, searchText.trim()));
      return;
    }

    // Adjust selectedMenu for regular suggestions (subtract categoryMatchCount)
    const adjustedSelectedMenu = selectedMenu >= categoryMatchCount ? selectedMenu - categoryMatchCount : -1;

    // The Function is triggered when the user presses the "Enter" button on the keyboard.
    // Query params are dynamically set based on current user input, If the user
    // enters good words and gets a response from the backend, then set module and category,
    // In other cases set alternatives or recent searches.
    const suggestionsCategories = suggestions && suggestions.categories ? suggestions.categories : [];
    const queryParams =
      suggestionsCategories[selectedMenu] &&
        suggestionsCategories[selectedMenu].name != ""
        ? {
          text: searchText,
          sortOption: AdsSortOption.mostRelevant,
          module: suggestionsCategories[selectedMenu].module,
          category: suggestionsCategories[selectedMenu].name
        }
        :
        enhancedSuggestions &&
          enhancedSuggestions.categoryMatchSuggestions &&
          enhancedSuggestions.categoryMatchSuggestions.length > 0
          ? {
            text: searchText,
            sortOption: AdsSortOption.mostRelevant,
            url: enhancedSuggestions.categoryMatchSuggestions[0].url,
          }
          :
          suggestions && suggestions.categories && suggestions.categories[0]
            ? {
              text: searchText,
              sortOption: AdsSortOption.mostRelevant,
              module: suggestions.categories[0].module,
              category: suggestions.categories[0].name
            }
            :
            suggestions && suggestions.notFound && alternatives && alternatives[0]
              ? { text: alternatives[0], sortOption: AdsSortOption.mostRelevant }
              : { text: searchText, sortOption: AdsSortOption.mostRelevant };

    // If the user press enter to search recent searches
    if (searchText.trim() == "" && recentSearches.length > 0) {
      if (JSON.stringify(recentQueryParams) == JSON.stringify({})) {
        return;
      } else if (recentQueryParams.url) {
        // AI recent searches have a pre-built URL
        history.push(appendSearchTracking(recentQueryParams.url, recentQueryParams.isAi ? SearchMode.ai : SearchMode.classic, recentQueryParams.text));
      } else {
        history.push(
          appendSearchTracking(`${recentQueryParams.module
            ? `/${mapModuleToHr(recentQueryParams.module)}`
            : ""
            }${recentQueryParams.category
              ? `/${mapCategoryToHr(
                recentQueryParams.category,
                recentQueryParams.module
              )}`
              : ""
            }${PageRoute.search}${stringifyURI(recentQueryParams, QueryParam.searchQuery)}`, SearchMode.classic, recentQueryParams.text)
        );
      }
    }

    if (searchText.trim()) {
      addRecentSearch(searchText, !all && queryParams.module, !all && queryParams.category);
      clearCriteriaModules();
      setError(false);
      setShowMobileStyle(false);
      addRootScrollBar();

      // GA4 event for tracking search terms
      trackViewingOfSearchResults({
        search_term: searchText.trim(),
        category: all ? "all-category" : (queryParams.category || ""),
        results_count: suggestions && suggestions.hits ? suggestions.hits : 0,
        search_type: SearchMode.classic
      })

      handleSuggestedOptionTracking()
      if (all) {
        history.push(
          appendSearchTracking(`${PageRoute.search}${stringifyURI({
            text: queryParams.text,
            sortOption: queryParams.sortOption
          }, QueryParam.searchQuery)}`, SearchMode.classic, searchText.trim())
        );
      } else if (queryParams.url) {
        history.push(appendSearchTracking(queryParams.url, SearchMode.classic, searchText.trim()))
      } else {
        history.push(
          appendSearchTracking(`${queryParams.module ? `/${mapModuleToHr(queryParams.module)}` : ""
            }${queryParams.category
              ? `/${mapCategoryToHr(queryParams.category, queryParams.module)}`
              : ""
            }${PageRoute.search}${stringifyURI(queryParams, QueryParam.searchQuery)}`, SearchMode.classic, searchText.trim())
        );
      }
    } else {
      setError(true);
    }
    handleInputBlur();
  };

  const handleTextChange = e => {
    const { value } = e.target;
    clearTimeout(getSuggestionTimeoutRef.current);

    // In AI mode, don't fetch suggestions while typing
    if (!isAiMode) {
      // Show loading indicator immediately when text is long enough
      if (value && value.length > 2) {
        setLoading(true);
      }
      getSuggestionTimeoutRef.current = setTimeout(() => {
        setLoading(value && value.length > 2);
        // backend expect value in lower case
        const timestamp = Date.now();
        getSuggestions(value.trim().toLowerCase(), timestamp);
        setActionTime(timestamp);
      }, unfocusTimeout);
    }
    setSearchText(value);
    setSelectedMenu(-1);
    setAiSelectedMenu(-1);
    setShowAllCategories(false);
  };

  const handleInputBlur = () => {
    setIsFocus(false);
    // @ts-ignore
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleKeyUp = useHandleKeyUp(
    handleSearch,
    tempSearchResult,
    recentSearches,
    searchText,
    setSearchText,
    setSelectedMenu,
    alternatives,
    setRecentQueryParams,
    enhancedSuggestions
  )


  const handleInputFocus = () => {
    const shouldIgnoreFocus = isMobile ?
      isFocus && showMobileStyle
      : isFocus && searchText !== "" && previousSearchText === searchText

    if (shouldIgnoreFocus) {
      return
    }
    // Only track GA event on first focus, not on subsequent keystrokes
    if (!isFocus) {
      trackClickEvent(isAiMode);
    }
    setError(false);
    setIsFocus(true);

    // In AI mode, don't fetch suggestions on focus
    if (!isAiMode) {
      setLoading(searchText && searchText.length > 2);
      clearTimeout(getSuggestionTimeoutRef.current);

      getSuggestionTimeoutRef.current = setTimeout(() => {

        setLoading(searchText && searchText.length > 2);
        // backend expect value in lower case
        const timestamp = Date.now();
        getSuggestions(searchText.trim().toLowerCase(), timestamp);
        setActionTime(timestamp);
      }, unfocusTimeout);
    }

    setShowMobileStyle(isMobile);
    if (isMobile) {
      if (!isHomePage) {
        removeRootScrollBar(); // search icon fix
      }
      inputRef.current ? focusToInput() : triggerRenderedInput();
    }

  };

  function focusToInput() {
    if (inputRef && inputRef.current && inputRef.current.input) {
      const input = inputRef.current.input;
      // Only focus if not already focused to prevent cursor position reset
      if (document.activeElement !== input) {
        input.focus();
        // Set cursor to end only on initial focus
        const length = input.value ? input.value.length : 0;
        input.setSelectionRange(length, length);
      }
    }
  }

  const addRecentSearch = (text, module?, category?, url?, isAi?) => {

    if (!text) return;
    const normalizedText = text.trim().toLowerCase();
    const localRecentSearches = localStorage.getItem(LocalStorageKey.recentSearches);
    let newRecentSearches = localRecentSearches || [];

    // Remove any existing entry with the same text (regardless of AI or classic mode)
    newRecentSearches = newRecentSearches.filter(
      s => s.text.trim().toLowerCase() !== normalizedText
    );

    const entry = { text, time: Date.now(), isAi: !!isAi };

    if (module && category) {
      entry.module = module;
      entry.category = category;
    }

    if (url) {
      entry.url = url;
    }

    newRecentSearches.unshift(entry);

    newRecentSearches = newRecentSearches.slice(0, config.localStorageLimits.recentSearches);

    localStorage.setItem(LocalStorageKey.recentSearches, newRecentSearches);
    setRecentSearches(newRecentSearches);
  };


  const handleBackClick = () => {
    // Go back in history to remove the state we pushed for mobile search
    window.history.back();
  };


  const removeRootScrollBar = () => {
    rootElement.style.setProperty(StyleProperty.overflowY, "hidden");
    rootElement.style.setProperty(StyleProperty.height, "100dvh");
  };

  const replaceMobilePlaceholder = text => {
    // Depending on screen width, we are showing the whole sentence
    // in two rows if the width is smaller than 768 px
    if (config.localeCode === LocaleCode.hr) {
      return text.concat(` <strong>${`"${alternatives[0]}"`}</strong> ?`);
    } else if (config.localeCode === LocaleCode.en) {
      return text.concat(` <strong>${`"${alternatives[0]}"`}</strong> ?`);
    }
  };

  const renderRecentSearch = (item, index) => {
    const handlesearchRecent = item => {
      const { clearCriteriaModules } = props;

      // GA4 event for tracking recent search selection V2OGLASI-2022
      handleRecentSearchTracking();
      trackViewingOfSearchResults({
        search_term: item.text ? item.text.trim() : "",
        category: item.isAi ? "ai-search" : (item.category || ""),
        search_type: item.isAi ? SearchMode.ai : SearchMode.classic
      })

      clearCriteriaModules();
      setError(false);
      setShowMobileStyle(false);
      setLoading(false);
      addRootScrollBar();
      handleInputBlur();

      // Recent searches with a pre-built URL (AI or category)
      if (item.url) {
        setSearchText(item.text);
        history.push(appendSearchTracking(item.url, item.isAi ? SearchMode.ai : SearchMode.classic, item.text));
        return;
      }

      const queryParams = {
        text: item.text,
        module: item.module,
        category: item.category,
        sortOption: AdsSortOption.mostRelevant,
      }

      history.push(
        appendSearchTracking(`${queryParams.module ? `/${mapModuleToHr(queryParams.module)}` : ""}${queryParams.category
          ? `/${mapCategoryToHr(queryParams.category, queryParams.module)}`
          : ""
          }${PageRoute.search}${stringifyURI(queryParams, QueryParam.searchQuery)}`, SearchMode.classic, item.text)
      );
    };

    const deleteRecentSearch = item => {
      const newRecentSearches = recentSearches.filter(r => r !== item);
      localStorage.setItem(LocalStorageKey.recentSearches, newRecentSearches);
      setRecentSearches(newRecentSearches);
      focusToInput();
    };


    return (
      <Menu.Item
        key={index}
        className={`${styles.optionContainer} ${styles.recent}`}
      >
        <span
          onClick={() => handlesearchRecent(item)}
          className={styles.recentOption}
        >
          <span className={styles.text}>{item.text}</span>
          <div className={styles.recentTime}>
            <span className={styles.timeLabel}>
              {"prije"} {moment(item.time).fromNow(true)}
            </span>
            <span
              className={styles.deleteRecentSearchButton}
              role="button"
              tabIndex={-1}
              onClick={e => {
                deleteRecentSearch(item);
                e.stopPropagation();
              }}
            >
              <img src={getIconUrl("delete-recent.svg")} alt="delete" />
            </span>
          </div>
        </span>
      </Menu.Item>
    );
  };


  if (iconOnly && !showMobileStyle) {
    return <SearchBarIconOnly isHomePage={isHomePage} handleInputFocus={handleInputFocus} />
  }


  const onBlurInput = () => {
    // @ts-ignore
    inputRef.current.blur();
  };

  const isDisabledByLoading = aiLoading || isSearchLoading;
  let fadeCondition = isMobile ? isFocus && searchText.length > 0 && showMobileStyle : isFocus && searchText.length > 0;

  const handleSearchClick = () => {
    if (isAiMode) {
      if (isFocus) {
        // If browsing recent searches (empty input), use classic search for non-AI entries
        if (searchText.trim() === "" && recentSearches.length > 0 && !recentQueryParams.isAi) {
          handleSearch();
        } else {
          handleAiSearch();
        }
      } else if (searchText.trim()) {
        // Not focused but has text - perform AI search
        handleAiSearch();
      } else if (isMobile) {
        handleInputFocus();
      }
    } else {
      // Classic mode - search if there's text, otherwise focus on mobile
      if (searchText.trim()) {
        handleSearch();
      } else if (isMobile) {
        handleInputFocus();
      }
    }
  };

  const dropdownCondition = !isAiMode && isFocus && ((searchText.trim() === "" && recentSearches.length > 0) || searchText.trim().length >= 3);
  // In AI mode, only show recent searches dropdown when input is empty
  const aiRecentCondition = isAiMode && isFocus && searchText.trim() === "" && recentSearches.length > 0;
  // In AI mode, show most-searched suggestions once the input reaches 3 characters
  const aiSuggestionCondition = isAiMode && isFocus && searchText.trim().length >= 3;
  // Collapse the AI dropdown entirely when the query returned nothing —
  // showing an empty-state row for a search-that-came-back-empty adds noise
  // instead of value (V2OGLASI-2498).
  const aiSuggestionDropdownOpen =
    aiSuggestionCondition && (aiSuggestionsLoading || aiSuggestions.length > 0);
  const canNavigateAiSuggestions =
    aiSuggestionCondition && !aiSuggestionsLoading && aiSuggestions.length > 0;

  const handleAiSuggestionSearch = (suggestion: string) => {
    ReactGA.event({
      category: "Tekstualna pretraga",
      action: "Click",
      label: "Odabir ponuđene opcije - AI"
    });


    handleSuggestedOptionTracking();
    setAiSelectedMenu(-1);
    handleAiSearch(suggestion);
  };

  const handleSearchKeyUp = (e) => {
    if (isAiMode && e.key === "Enter") {
      // A highlighted suggestion wins over the typed text
      if (canNavigateAiSuggestions && aiSelectedMenu >= 0 && aiSelectedMenu < aiSuggestions.length) {
        e.preventDefault();
        handleAiSuggestionSearch(aiSuggestions[aiSelectedMenu]);
        return;
      }
      // If browsing recent searches (empty input), use classic search for non-AI entries
      if (searchText.trim() === "" && recentSearches.length > 0 && !recentQueryParams.isAi) {
        handleSearch();
        return;
      }
      e.preventDefault();
      handleAiSearch();
      return;
    }

    // Arrow navigation over the AI suggestions, wrapping back to "nothing highlighted"
    if (canNavigateAiSuggestions && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      const lastIndex = aiSuggestions.length - 1;
      setAiSelectedMenu(state =>
        e.key === "ArrowDown"
          ? (state >= lastIndex ? -1 : state + 1)
          : (state <= -1 ? lastIndex : state - 1)
      );
      return;
    }

    handleKeyUp(e);
  };

  return (
    <span
      className={`child-center ${className} ${showMobileStyle ? styles.mobile : styles.wrapper
        }`}
    >
      {isAiMode && isHomePage && !isFocus && !glowAnimationPlayed && <div className={styles.aiGlow} />}
      <div
        className={`${!isMobile || showMobileStyle
          ? styles.searchBar
          : styles.searchBarMobile
          } ${isAiMode && isHomePage && !showMobileStyle ? styles.aiMode : ''} ${isAiMode && isHomePage && glowAnimationPlayed ? styles.aiModeAnimationDone : ''} ${(dropdownCondition || aiRecentCondition || aiSuggestionDropdownOpen) ? styles.dropdownOpen : ''}`}
      >
        {/* Mobile: Input row wrapper */}
        {showMobileStyle && (
          <div className={`${styles.mobileInputRow} ${aiLoading ? styles.mobileInputRowLoading : ''}`}>
            <span className={styles.mobileSearchIcon} onClick={handleBackClick}>
              <ChevronLeftIcon className={styles.mobileSearchIconSvg} />
            </span>
            <SearchInput
              className={styles.input}
              placeholder={isAiMode ? "" : "Upišite pojam"}
              onChange={handleTextChange}
              onClick={(e: any) => {
                e.stopPropagation();
                handleInputFocus();
              }}
              onKeyUp={handleSearchKeyUp}
              value={searchText}
              error={error || null}
              ref={inputRef}
              isMobile={isMobile}
              readOnly={aiLoading}
            />
            {/* Animated placeholder for mobile AI mode — hidden on focus so
                it doesn't fight with the real caret once the user commits. */}
            {showAnimatedPlaceholderMobile && (
              <div className={styles.mobileAnimatedPlaceholder}>
                <span className={`${styles.cursor} ${cursorVisible ? styles.cursorVisible : styles.cursorHidden}`}>|</span>
                <span>{placeholderText}</span>
              </div>
            )}
            {searchText.trim() !== "" && (
              <span
                className={styles.mobileCloseButton}
                onClick={() => {
                  setSearchText("");
                  focusToInput();
                }}
              >
                <CloseIcon className={styles.mobileCloseIcon} />
              </span>
            )}
          </div>
        )}
        {/* Desktop: Input */}
        {!showMobileStyle && (
          <SearchInput
            className={styles.input}
            placeholder={isAiMode && isHomePage ? "" : "Upišite pojam"}
            onChange={isDisabledByLoading ? undefined : handleTextChange}
            // @ts-ignore
            onClick={(e: any) => {
              e.stopPropagation();
              if (!isDisabledByLoading) handleInputFocus();
            }}
            onKeyUp={isDisabledByLoading ? undefined : handleSearchKeyUp}
            value={searchText}
            error={error || null}
            ref={inputRef}
            isMobile={isMobile}
            readOnly={isDisabledByLoading}
            style={isDisabledByLoading ? { color: '#ccc', pointerEvents: 'none' } : undefined}
          />
        )}
        {/* Animated placeholder for AI mode - desktop only */}
        {showAnimatedPlaceholderDesktop && !showMobileStyle && (
          <div className={styles.animatedPlaceholder}>
            <span>{placeholderText}</span>
            <span className={`${styles.cursor} ${cursorVisible ? styles.cursorVisible : styles.cursorHidden}`}>|</span>
          </div>
        )}
        {/* Desktop: Delete text button */}
        {!showMobileStyle && !isMobile && !isDisabledByLoading && (
          <span
            data-prevent-close
            className={`${styles.deleteText} ${fadeCondition ? styles.fadeIn : styles.fadeOut
              }`}
            onClick={() => {
              fadeCondition && setSearchText("");
              setTempSearchResult(emptyObject.current)
              focusToInput()
            }}
          >
            <img src={getIconUrl("remove.svg")} alt="x" />
          </span>
        )}

        {/* Desktop: Search mode toggle dropdown */}
        {!showMobileStyle && !isMobile && (
          <div
            ref={searchModeDropdownRef}
            className={styles.searchModeToggle}
            data-prevent-close
          >
            <span className={styles.searchModeSeparator} />
            <span
              role="button"
              tabIndex={0}
              className={styles.searchModeButton}
              onClick={(e) => {
                e.stopPropagation();
                setShowSearchModeDropdown(!showSearchModeDropdown);
              }}
            >
              <span className={styles.searchModeLabel}>
                {isAiMode ? "AI" : "Klasična"}
              </span>
              {isAiMode && <span className={styles.betaBadge}>BETA</span>}
              <img
                src={getIconUrl("arrow-down.svg")}
                alt="toggle"
                className={`${styles.searchModeChevron} ${showSearchModeDropdown ? styles.searchModeChevronOpen : ''}`}
              />
            </span>
            {showSearchModeDropdown && (
              <div className={styles.searchModeDropdown}>
                <div
                  className={`${styles.searchModeOption} ${isAiMode ? styles.searchModeOptionActive : ''}`}
                  onClick={() => {
                    setSearchMode(SearchMode.ai);
                    trackSearchModeChange(true);
                    setShowSearchModeDropdown(false);
                    setIsFocus(true);
                    setTimeout(() => focusToInput(), 50);
                  }}
                >
                  AI
                  <span className={styles.betaBadgeSmall}>BETA</span>
                </div>
                <div
                  className={`${styles.searchModeOption} ${!isAiMode ? styles.searchModeOptionActive : ''}`}
                  onClick={() => {
                    setSearchMode(SearchMode.classic);
                    trackSearchModeChange(false);
                    setShowSearchModeDropdown(false);
                    setIsFocus(true);
                    // Fetch suggestions for Classic mode
                    if (searchText && searchText.trim().length > 2) {
                      setLoading(true);
                      const timestamp = Date.now();
                      getSuggestions(searchText.trim().toLowerCase(), timestamp);
                      setActionTime(timestamp);
                    }
                    setTimeout(() => focusToInput(), 50);
                  }}
                >
                  Klasična
                </div>
              </div>
            )}
          </div>
        )}

        {/* Desktop/Mobile Homepage: Search button */}
        {!showMobileStyle && (
          <span
            role="button"
            tabIndex={0}
            className={`${isMobile ? styles.searchButtonMobile : styles.searchButton} ${isAiMode && !isMobile ? styles.searchButtonAi : ''}`}
            onClick={isDisabledByLoading ? undefined : handleSearchClick}
          >
            {isDisabledByLoading ? (
              <div className={styles.searchButtonSpinner} />
            ) : (
              <img
                alt={"Traži"}
                src={getIconUrl("search-white.svg")}
              />
            )}
          </span>
        )}

        {/* Mobile: Gray line below input */}
        {showMobileStyle && (
          <div className={styles.mobileGrayLine} />
        )}

        {/* Mobile: Radio buttons for search mode */}
        {showMobileStyle && (
          <div className={styles.mobileSearchModeRadio}>
            <label className={`${styles.mobileRadioOption} ${isAiMode ? styles.mobileRadioOptionActive : ''}`}>
              <input
                type="radio"
                name="searchMode"
                checked={isAiMode}
                onChange={() => { setSearchMode(SearchMode.ai); trackSearchModeChange(true); setIsFocus(true); setTimeout(() => focusToInput(), 50); }}
              />
              <span className={styles.mobileRadioCircle} />
              <span className={styles.mobileRadioLabel}>AI pretraga</span>
              <span className={styles.betaBadge}>BETA</span>
            </label>
            <label className={`${styles.mobileRadioOption} ${!isAiMode ? styles.mobileRadioOptionActive : ''}`}>
              <input
                type="radio"
                name="searchMode"
                checked={!isAiMode}
                onChange={() => {
                  setSearchMode(SearchMode.classic);
                  trackSearchModeChange(false);
                  setIsFocus(true);
                  // Fetch suggestions for Classic mode
                  if (searchText && searchText.trim().length > 2) {
                    setLoading(true);
                    const timestamp = Date.now();
                    getSuggestions(searchText.trim().toLowerCase(), timestamp);
                    setActionTime(timestamp);
                  }
                  setTimeout(() => focusToInput(), 50);
                }}
              />
              <span className={styles.mobileRadioCircle} />
              <span className={styles.mobileRadioLabel}>Klasična pretraga</span>
            </label>
          </div>
        )}


        <div
          ref={menuRef}
          onTouchMove={onBlurInput}
          className={styles.manuWrapper}
        >
          {/* Mobile AI: Animated line with glow */}
          {showMobileStyle && isAiMode && (
            <div className={`${styles.mobileAiLineWithGlow} ${glowAnimationPlayed ? styles.mobileAiLineWithGlowDone : ''} ${aiLoading ? styles.mobileAiLineLoading : ''}`}>
              <div className={styles.mobileAiLineBar} />
              <div className={styles.mobileAiLineGlowWrapper}>
                <div className={styles.mobileAiLineGlow} />
                <div className={styles.mobileAiLineGlowFade} />
              </div>
            </div>
          )}
          {/* Mobile Classic: Gray line without glow */}
          {showMobileStyle && !isAiMode && (
            <div className={styles.mobileClassicLine} />
          )}
          {/* AI mode: show recent searches when input is empty */}
          {aiRecentCondition && (
            <Menu
              className={styles.suggestionList}
              selectedKeys={[selectedMenu != null && selectedMenu.toString()]}
            >
              <Menu.Item key="recent-header" className={styles.optionContainer} disabled>
                <div className={styles.sectionHeader}>
                  {"Nedavne pretrage"}
                </div>
              </Menu.Item>
              {recentSearches.map((recent, index) =>
                renderRecentSearch(recent, index)
              )}
            </Menu>
          )}
          {/* AI mode: show most-searched suggestions once there are 3+ characters.
              We hide the dropdown entirely when the query returned nothing. */}
          {aiSuggestionDropdownOpen && (
            <Menu
              className={styles.suggestionList}
              selectedKeys={[aiSelectedMenu.toString()]}
            >
              {aiSuggestionsLoading ? (
                <Menu.Item key="ai-suggestions-loading" className={styles.optionContainer} disabled>
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner} />
                    <span className={styles.loadingText}>
                      {"Tražimo najčešće pojmove"}
                    </span>
                  </div>
                </Menu.Item>
              ) : [
                <Menu.Item key="ai-suggestions-header" className={styles.optionContainer} disabled>
                  <div className={`${styles.sectionHeader} ${styles.sectionHeaderFirst}`}>
                    {"Najčešće pretrage"}
                  </div>
                </Menu.Item>,
                aiSuggestions.map((suggestion, index) => (
                  <Menu.Item key={index} className={styles.optionContainer}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => handleAiSuggestionSearch(suggestion)}
                      className={styles.option}
                    >
                      {suggestion}
                    </span>
                  </Menu.Item>
                ))
              ]}
            </Menu>
          )}
          {/* Classic mode: show suggestions dropdown */}
          {dropdownCondition &&
            (loading ? (
              <Menu
                className={styles.suggestionList}
                selectedKeys={[selectedMenu != null && selectedMenu.toString()]}
              >
                <Menu.Item key="loading" className={styles.optionContainer} disabled>
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner} />
                    <span className={styles.loadingText}>
                      {"Pretražujemo najbolje oglase za vas"}
                    </span>
                  </div>
                </Menu.Item>
              </Menu>
            ) : (
              <Menu
                className={styles.suggestionList}
                selectedKeys={[selectedMenu != null && selectedMenu.toString()]}
              >
                {/* Render recent searches if there is no text entered */}
                {searchText.trim() === "" && recentSearches.length > 0 && (
                  <Menu.Item key="recent-header" className={styles.optionContainer} disabled>
                    <div className={styles.sectionHeader}>
                      {"Vaše prethodne pretrage"}
                    </div>
                  </Menu.Item>
                )}
                {searchText.trim() === "" &&
                  recentSearches.map((recent, index) =>
                    renderRecentSearch(recent, index)
                  )}

                {/* Render CategoryMatch suggestions FIRST */}
                {enhancedSuggestions &&
                  enhancedSuggestions.categoryMatchSuggestions &&
                  enhancedSuggestions.categoryMatchSuggestions.length > 0 && [
                    <Menu.Item key="category-header" className={styles.optionContainer} disabled>
                      <div className={`${styles.sectionHeader} ${styles.sectionHeaderFirst}`}>
                        {"Kategorije"}
                      </div>
                    </Menu.Item>,
                    enhancedSuggestions.categoryMatchSuggestions.map((item, index) =>
                      <Menu.Item key={index} className={styles.optionContainer}>
                        <CategoryMatchSuggestion
                          displayText={item.displayText}
                          url={item.url}
                          searchText={searchText}
                          setError={setError}
                          setShowMobileStyle={setShowMobileStyle}
                          addRootScrollBar={addRootScrollBar}
                          handleInputBlur={handleInputBlur}
                          addRecentSearch={addRecentSearch}
                        />
                      </Menu.Item>
                    )
                  ]}
                {/* Render text search suggestions */}
                {suggestions &&
                  !suggestions.notFound &&
                  suggestions.categories && suggestions.categories.length > 0 && [
                    <Menu.Item key="terms-header" className={styles.optionContainer} disabled>
                      <div className={`${styles.sectionHeader} ${!(enhancedSuggestions && enhancedSuggestions.categoryMatchSuggestions && enhancedSuggestions.categoryMatchSuggestions.length) ? styles.sectionHeaderFirst : ''}`}>
                        {"Pojmovi"}
                      </div>
                    </Menu.Item>,
                    <Menu.Item key="search-all" className={styles.optionContainer}>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSearch("all")}
                        className={styles.option}
                      >
                        <strong>{`${searchText}`}</strong>&nbsp;{"u svim kategorijama"}&nbsp;
                        <span>({suggestions.hits})</span>
                      </span>
                    </Menu.Item>,
                    suggestions.categories
                      ? (showAllCategories
                        ? suggestions.categories
                        : suggestions.categories.slice(0, 3)
                      ).map((suggestion, i) => (
                        <Menu.Item key={categoryMatchCount + i} className={styles.optionContainer}>
                          <SearchBarSuggestion
                            addRecentSearch={addRecentSearch}
                            searchText={searchText}
                            suggestions={suggestions}
                            item={suggestion}
                            setError={setError}
                            setShowMobileStyle={setShowMobileStyle}
                            addRootScrollBar={addRootScrollBar}
                            handleInputBlur={handleInputBlur}
                            {...props}
                          />
                        </Menu.Item>
                      ))
                      : null,
                    !showAllCategories && suggestions.categories && suggestions.categories.length > 3 && (
                      <Menu.Item key="show-more-categories" className={styles.optionContainer} disabled>
                        <div
                          data-prevent-close
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowAllCategories(true)}
                          className={styles.showAllLink}
                        >
                          {"Prikaži više"}
                        </div>
                      </Menu.Item>
                    )
                  ]}
                {/* Render message that tehere is 0 suggestions founded but show alternatives */}
                {suggestions && suggestions.notFound && alternatives[0] && (
                  <Menu.Item key={-2} className={styles.optionContainer}>
                    <span
                      role="button"
                      tabIndex={0}
                      className={styles.suggestionTitle}
                    >
                      {"Za traženu pretragu nema rezultata."}
                    </span>
                  </Menu.Item>
                )}
                {/* Show alternative search */}
                {suggestions && suggestions.notFound && alternatives[0] && (
                  <Menu.Item key={-1} className={styles.optionContainer}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => searchAlternatives(alternatives[0])}
                      className={styles.option}
                    >
                      {" "}
                      {/* Show in two lines if the screen width is smaller than 768 px */}
                      {window.innerWidth < 768 ? (
                        <StaticHTML
                          content={replaceMobilePlaceholder(
                            "Jeste li mislili:"
                          )}
                        />
                      ) : (
                        <StaticHTML
                          content={`${"Jeste li mislili:"} <strong> "${alternatives[0]}" </strong> ?`}
                        />
                      )}
                    </span>
                  </Menu.Item>
                )}
                {/* Render message that tehere is 0 suggestions founded */}
                {suggestions && suggestions.notFound && !alternatives[0] && (
                  <Menu.Item key="not-found" className={styles.optionContainer}>
                    <span
                      role="button"
                      tabIndex={0}
                      className={styles.suggestionTitle}
                    >
                      {"Za traženu pretragu nema rezultata."}
                    </span>
                  </Menu.Item>
                )}
              </Menu>
            ))}
        </div>
      </div>
    </span>
  );
}

SearchBar.defaultProps = {
  searchResult: {},
  className: "",
  isMobile: false,
  iconOnly: false
};

function mapStateToProps(state) {
  return {
    searchResult: selectSuggestions(state),
    searchText: selectSearchText(state),
    isFocus: selectIsFocus(state),
    isSearchLoading: selectSearchLoading(state),
    isAiMode: selectIsAiMode(state)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getSuggestions: (searchingValue, actionTime) =>
      dispatch(getSuggestions(searchingValue, actionTime)),
    setSearchText: text => dispatch(setSearchText(text)),
    clearCriteriaModules: () => dispatch(updateCriteriaModules(null)),
    setIsFocus: state => dispatch(setIsFocus(state)),
    setSearchLoadingRedux: state => dispatch(setSearchLoading(state)),
    setSearchMode: mode => dispatch(updateSearchMode(mode))
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(SearchBar));



// WEBPACK FOOTER //
// ./src/containers/HomePage/SearchBar.tsx